import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Pagination, Tooltip
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import { fetchData, postData, putData, deleteData } from "../../../BaseUrl";
import Toast from "../../../DynamicComponent/Toast";

const PAGE_SIZE = 10;

const emptyForm = { title: "", description: "", imageFile: null, imagePreview: "" };

function ConfirmDialog({ open, onClose, onConfirm, message }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>
          {message || "Are you sure you want to delete this item?"}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const FALLBACK = [
  { _id: "1", title: "Welcome to Cumming Tamil School", description: "Join us to learn Tamil language and culture.", imageUrl: "" },
  { _id: "2", title: "Annual Day 2025", description: "Celebrating our students' achievements.", imageUrl: "" },
  { _id: "3", title: "New Semester Starts", description: "Enroll your children for the new academic year.", imageUrl: "" },
];

function HomeConfig() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/home-config/list");
      setRows(Array.isArray(res?.data) ? res.data : FALLBACK);
    } catch {
      setRows(FALLBACK);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const openAdd = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({ title: row.title || "", description: row.description || "", imageFile: null, imagePreview: row.imageUrl || "" });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setForm(emptyForm); setEditId(null); };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(prev => ({ ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast("error", "Title is required."); return; }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    if (form.imageFile) fd.append("image", form.imageFile);
    try {
      if (editId) {
        await putData(`admin/home-config/update/${editId}`, fd);
        showToast("success", "Carousel item updated.");
      } else {
        await postData("admin/home-config/create", fd);
        showToast("success", "Carousel item added.");
      }
      closeDialog();
      load();
    } catch {
      showToast("error", "Failed to save. Please try again.");
    }
  };

  const askDelete = (row) => { setDeleteTarget(row); setConfirmOpen(true); };
  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/home-config/${id}`);
      showToast("success", "Item deleted.");
      load();
    } catch {
      showToast("error", "Failed to delete.");
    }
  };

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const btnStyle = {
    background: "linear-gradient(90deg, #4A7BA8, #244C77)",
    color: "#fff", fontSize: "13px", textTransform: "none",
    borderRadius: "10px", px: 2.5, py: 1,
    "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete} message="Are you sure you want to delete this carousel item?" />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Home / Carousel Cards displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ViewCarouselIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>
              Carousel Management
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Manage carousel cards displayed on the home page
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<AddPhotoAlternateIcon />} onClick={openAdd} sx={btnStyle}>
          + Add Carousel
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Image", "Title", "Description", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#9CA3AF", fontSize: "14px" }}>
                    No carousel items yet. Click "+ Add Carousel" to add one.
                  </TableCell>
                </TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || row.id || idx}
                  sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", width: 60 }}>
                    {(page - 1) * PAGE_SIZE + idx + 1}
                  </TableCell>
                  <TableCell sx={{ width: 80 }}>
                    {row.imageUrl ? (
                      <img src={row.imageUrl} alt={row.title}
                        style={{ width: 60, height: 60, objectFit: "cover", borderRadius: "8px", border: "1px solid #E5E7EB" }} />
                    ) : (
                      <Box sx={{ width: 60, height: 60, borderRadius: "8px", backgroundColor: "#F3F4F6",
                        display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #D1D5DB" }}>
                        <AddPhotoAlternateIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", maxWidth: 200 }}>
                    {row.title}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#6B7280", maxWidth: 300 }}>
                    <Typography noWrap sx={{ fontSize: "13px", maxWidth: 280 }}>
                      {row.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}
                          sx={{ color: "#244C77", "&:hover": { backgroundColor: "#EEF2FF" } }}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => askDelete(row)}
                          sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEF2F2" } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page}
              onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px", color: "#111827" }}>
          {editId ? "Edit Carousel Item" : "Add Carousel Item"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          {/* Image Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151", mb: 0.8 }}>Image</Typography>
            <Box
              component="label"
              sx={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                border: "2px dashed #D1D5DB", borderRadius: "10px", height: 160, cursor: "pointer",
                backgroundColor: "#F9FAFB", "&:hover": { borderColor: "#4A7BA8" }, position: "relative", overflow: "hidden",
              }}>
              {form.imagePreview ? (
                <img src={form.imagePreview} alt="preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }} />
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: "40px", color: "#9CA3AF", mb: 1 }} />
                  <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Click to upload image</Typography>
                </>
              )}
              <input type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleImageChange} />
            </Box>
          </Box>
          <TextField label="Title" fullWidth size="small" value={form.title}
            onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Description" fullWidth size="small" multiline rows={3} value={form.description}
            onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained"
            sx={{ ...btnStyle, px: 3, py: 0.8 }}>
            {editId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default HomeConfig;
